import gaiupApolloClient from "@/graphql/services/ApolloClient";
import {
  ReplaceProjectBuildingAreasDocument,
  ReplaceProjectBuildingAreasWithVersionDocument,
  type ReplaceProjectBuildingAreasMutation,
  type ReplaceProjectBuildingAreasMutationVariables,
  type ReplaceProjectBuildingAreasWithVersionMutation,
  type ReplaceProjectBuildingAreasWithVersionMutationVariables,
} from "@/graphql";

import { withVersionFallback } from "./projectSetupWrites";
import { buildBuildingAreasVersionInput, buildingAreaRows, type ProjectBuildingAreasWriteInput } from "./buildingAreaWriteInput";

export interface ProjectBuildingAreasWriteResult {
  rowIds: string[];
  /** false when the trail row could not be written and the plain document was used instead. */
  versionLogged: boolean;
}

export interface BuildingAreasVersionMeta {
  /** The saved baseline, so the trail row can say what moved; null logs all six fields. */
  previous: ProjectBuildingAreasWriteInput | null;
  /** Names a building type in the trail row's list of uses. */
  labelFor?: (buildingTypeId: number) => string;
}

/**
 * The project's building areas — GFA, floor counts and heated areas — written through the user role
 * with a trail row, so a settings save that changed nothing else no longer needs the REST
 * `updatecalculationassumptions` PUT for them (RTLCA-5781).
 *
 * That PUT is what this replaces, and why: its handler replays the project's Marten aggregate, and a
 * project created through `POST api/BuildingProject/base` has no Marten stream at all, so the call
 * 404s and the areas are lost. These rows are also what the API composes its own aggregate FROM
 * (`BuildingProjectFactory.ToEmissionCalculationAssumptions` / `ToReferencedAreas`), so a
 * client-written area is authoritative for the calculation rather than a copy of it.
 *
 * Replaced rather than upserted: a mixed-use project stores an above- and a
 * below-ground row per building use, so the set of rows itself changes when a use is added, removed
 * or retyped. A single use is one pair under the project's building type.
 */
export const replaceProjectBuildingAreas = async (
  input: ProjectBuildingAreasWriteInput,
  meta: BuildingAreasVersionMeta
): Promise<ProjectBuildingAreasWriteResult> => {
  const areas = buildingAreaRows(input);

  const map = (
    data: ReplaceProjectBuildingAreasMutation | ReplaceProjectBuildingAreasWithVersionMutation,
    versionLogged: boolean
  ): ProjectBuildingAreasWriteResult => {
    if (!data.inserted) throw new Error("The building area rows were not returned by Hasura.");
    return {
      rowIds: data.inserted.returning.map((row) => row.id),
      versionLogged,
    };
  };

  return withVersionFallback<
    ReplaceProjectBuildingAreasWithVersionMutation,
    ReplaceProjectBuildingAreasMutation,
    ProjectBuildingAreasWriteResult
  >(
    async () =>
      (
        await gaiupApolloClient.mutate<
          ReplaceProjectBuildingAreasWithVersionMutation,
          ReplaceProjectBuildingAreasWithVersionMutationVariables
        >({
          mutation: ReplaceProjectBuildingAreasWithVersionDocument,
          variables: {
            projectId: input.projectId,
            areas,
            version: buildBuildingAreasVersionInput(input, meta.previous, meta.labelFor),
          },
        })
      ).data,
    async () =>
      (
        await gaiupApolloClient.mutate<ReplaceProjectBuildingAreasMutation, ReplaceProjectBuildingAreasMutationVariables>({
          mutation: ReplaceProjectBuildingAreasDocument,
          variables: { projectId: input.projectId, areas },
        })
      ).data,
    (data) => map(data, true),
    (data) => map(data, false)
  );
};
